import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'antd';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { SelectPidType } from '@/entities/personalIdentifiers';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { PersonalIdentifierType } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { EditUnregBeneficiaryModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { UnregBeneficiaryData } from '../CreateBeneficiaryModalV2/types';
import {
  InterwillFieldsLineWrapper,
  InterwillFormContainer,
  InterwillStyledForm,
  InterwillSubmitButtonWrapper,
} from '../CreateEntityModal/styled';

export const EditUnregBeneficiaryModal: FC<EditUnregBeneficiaryModalProps> = ({
  fields,
  beneficiaryData,
  updateBeneficiaryData,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'editUnregBeneficiary' });
  const isDesktop = useIsDesktop();
  const modals = useModalsStore();

  const title = useMemo(() => {
    switch (fields) {
      case 'citizenship':
        return t('citizenshipTitle');
      case 'residence':
      default:
        return t('residenceTitle');
    }
  }, []);

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState,
  } = useForm<UnregBeneficiaryData>({
    defaultValues: {
      ...beneficiaryData,
    },
  });

  const handleSubmit = async (values: UnregBeneficiaryData) => {
    updateBeneficiaryData(values);
    modals.closeModal();
  };

  return (
    <ModalContainerV1 title={title}>
      <InterwillStyledForm onFinish={formHandleSubmit(handleSubmit)}>
        <InterwillFormContainer className="uikitScrollbar">
          {fields === 'citizenship' && (
            <>
              <Controller
                name={'countryCitizenship'}
                control={control}
                render={({ field, fieldState }) => (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <SelectCountry
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                )}
              />
              <InterwillFieldsLineWrapper>
                <Controller
                  name={'documentType'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <SelectPidType
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          types={[
                            PersonalIdentifierType.PASSPORT,
                            PersonalIdentifierType.NATIONAL_ID,
                          ]}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  )}
                />
                <Controller
                  name={'documentNumber'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          maxLength={128}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  )}
                />
              </InterwillFieldsLineWrapper>
              <Controller
                name={'issueDate'}
                control={control}
                render={({ field, fieldState }) => (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <DatePicker placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                    </Form.Item>
                  </FieldContainerIw>
                )}
              />
            </>
          )}

          {fields === 'residence' && (
            <>
              <Controller
                name={'countryResidence'}
                control={control}
                render={({ field, fieldState }) => (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <SelectCountry
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                )}
              />
              <InterwillFieldsLineWrapper>
                <Controller
                  name={'district'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  )}
                />
                <Controller
                  name={'city'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  )}
                />
              </InterwillFieldsLineWrapper>
              <InterwillFieldsLineWrapper>
                <Controller
                  name={'street'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  )}
                />
                <Controller
                  name={'apartment'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  )}
                />
              </InterwillFieldsLineWrapper>
            </>
          )}
        </InterwillFormContainer>

        <InterwillSubmitButtonWrapper>
          <ButtonIw
            htmlType="submit"
            block={!isDesktop}
            loading={formState.isSubmitting}
            disabled={formState.isSubmitting}
          >
            {t('btn')}
          </ButtonIw>
        </InterwillSubmitButtonWrapper>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};
